/**
 * Single persona card component
 */

import type { Persona, PersonaId } from "../../types/persona";
import { PersonaAvatar } from "./PersonaAvatar";

interface PersonaCardProps {
  persona: Persona;
  isSelected: boolean;
  onSelect: (id: PersonaId) => void;
  showDescription?: boolean;
}

export function PersonaCard({
  persona,
  isSelected,
  onSelect,
  showDescription = false,
}: PersonaCardProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(persona.personaId)}
      className={`
        relative flex flex-col items-center p-4 rounded-md transition-all duration-200
        ${
          isSelected
            ? "bg-primary-container border-2 border-primary shadow-3 scale-105"
            : "bg-surface-container-lowest border-2 border-outline-variant hover:border-primary/50 hover:shadow-2"
        }
      `}
    >
      {/* Character image */}
      <div className="relative w-20 h-20 mb-3">
        <PersonaAvatar
          persona={persona}
          size="lg"
          className="border-3 border-surface-container-lowest shadow-2"
        />
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Name and emoji */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-1 font-semibold text-on-surface">
          <span>{persona.emoji}</span>
          <span>{persona.name}</span>
        </div>
        <p className="text-sm text-on-surface-variant mt-1">{persona.description}</p>
      </div>

      {/* Expanded description on hover/selection */}
      {showDescription && (
        <p className="text-xs text-on-surface-variant mt-2 text-center leading-relaxed">
          {persona.toneDescription}
        </p>
      )}
    </button>
  );
}
